import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../lib/api/axiosConfig';

// ── Constants ──────────────────────────────────────────────────────────────────
const PERIODS = [
  { key: 'jour',    label: 'Jour'    },
  { key: 'semaine', label: 'Semaine' },
  { key: 'mois',    label: 'Mois'    },
  { key: 'annee',   label: 'Année'   },
];

const SHORT_MONTHS = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Aoû','Sep','Oct','Nov','Déc'];
const LONG_MONTHS  = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

const DECL_COLOR  = '#10B981';
const USER_COLOR  = '#0284C7';
const GENRE_COLORS = {
  homme:  '#0284C7',
  femme:  '#EC4899',
  enfant: '#F59E0B',
  inconnu:'#9ca3af',
};

// ── Helpers ────────────────────────────────────────────────────────────────────
function getDateLabel(period, d) {
  if (period === 'jour') return `${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}. ${d.getFullYear()}`;
  if (period === 'semaine') {
    const dow   = ((d.getDay() + 6) % 7);
    const start = new Date(d); start.setDate(d.getDate() - dow);
    const end   = new Date(start); end.setDate(start.getDate() + 6);
    if (start.getMonth() === end.getMonth())
      return `${start.getDate()} - ${end.getDate()} ${SHORT_MONTHS[end.getMonth()]}. ${end.getFullYear()}`;
    return `${start.getDate()} ${SHORT_MONTHS[start.getMonth()]} - ${end.getDate()} ${SHORT_MONTHS[end.getMonth()]}. ${end.getFullYear()}`;
  }
  if (period === 'mois') return `${LONG_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  return String(d.getFullYear());
}

function isCurrentPeriod(period, d) {
  const now = new Date();
  if (period === 'jour')  return d.toDateString() === now.toDateString();
  if (period === 'mois')  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  if (period === 'annee') return d.getFullYear() === now.getFullYear();
  const dow = ((d.getDay() + 6) % 7);
  const ws  = new Date(d); ws.setDate(d.getDate() - dow);
  const we  = new Date(ws); we.setDate(ws.getDate() + 6);
  return now >= ws && now <= we;
}

function smoothPath(pts) {
  if (pts.length === 0) return '';
  if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
  const t = 0.3;
  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const cp1x = p1.x + (p2.x - p0.x) * t;
    const cp1y = p1.y + (p2.y - p0.y) * t;
    const cp2x = p2.x - (p3.x - p1.x) * t;
    const cp2y = p2.y - (p3.y - p1.y) * t;
    d += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}

// ── Area chart ─────────────────────────────────────────────────────────────────
function AreaChart({ series, color, gradId }) {
  const n = series.length;
  if (n === 0) return <p style={{ color: '#9ca3af', fontSize: 13 }}>Aucune donnée</p>;

  const PAD  = { t: 12, r: 16, b: 30, l: 12 };
  const H    = 130;
  const minW = Math.max(560, n * 22);
  const cW   = minW - PAD.l - PAD.r;
  const cH   = H - PAD.t - PAD.b;
  const maxV = Math.max(...series.map(s => s.value), 1);

  const pts = series.map((s, i) => ({
    x: PAD.l + (n > 1 ? (i / (n - 1)) * cW : cW / 2),
    y: PAD.t + (1 - s.value / maxV) * cH,
    v: s.value,
    l: s.label,
  }));

  const line = smoothPath(pts);
  const area = pts.length > 1
    ? `${line} L ${pts[n - 1].x.toFixed(1)} ${(H - PAD.b).toFixed(1)} L ${pts[0].x.toFixed(1)} ${(H - PAD.b).toFixed(1)} Z`
    : '';

  const labelStep = n > 20 ? 4 : n > 12 ? 2 : 1;

  return (
    <div style={{ overflowX: 'auto', marginTop: 8 }}>
      <svg width={minW} height={H} viewBox={`0 0 ${minW} ${H}`} style={{ display: 'block' }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {/* Grid */}
        {[0, 0.25, 0.5, 0.75, 1].map(v => (
          <line key={v}
            x1={PAD.l} y1={PAD.t + v * cH}
            x2={minW - PAD.r} y2={PAD.t + v * cH}
            stroke="#e5e7eb" strokeWidth="1"
          />
        ))}
        {/* Area */}
        {area && <path d={area} fill={`url(#${gradId})`} />}
        {/* Line */}
        {line && <path d={line} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />}
        {/* Dots */}
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3.5" fill={color} />
        ))}
        {/* Labels */}
        {pts.map((p, i) =>
          i % labelStep === 0 ? (
            <text key={i} x={p.x} y={H - 6} textAnchor="middle" fontSize="9" fill="#9ca3af">{p.l}</text>
          ) : null
        )}
      </svg>
    </div>
  );
}

// ── Small components ───────────────────────────────────────────────────────────
function StatCard({ label, total, color, children }) {
  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.08em', marginBottom: 2, textTransform: 'uppercase' }}>{label}</div>
          <div style={{ fontSize: 38, fontWeight: 800, color, lineHeight: 1.1 }}>{total ?? '—'}</div>
        </div>
        <div style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: color, marginTop: 8 }} />
      </div>
      {children}
    </div>
  );
}

function ProgressRow({ label, count, total, color, dot }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
        {dot && <div style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: color, marginRight: 8, flexShrink: 0 }} />}
        <span style={{ flex: 1, fontSize: 13, color: '#2c2c2c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#2c2c2c', marginLeft: 8 }}>{count}</span>
      </div>
      <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, backgroundColor: color, borderRadius: 3, transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div style={card}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 16 }}>{title}</div>
      {children}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────
const card = {
  background: '#ffffff',
  borderRadius: 12,
  padding: '1.25rem',
  marginBottom: '1rem',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
};

// ── Main component ─────────────────────────────────────────────────────────────
export default function AdminDashboardTab() {
  const [period,      setPeriod]      = useState('jour');
  const [refDate,     setRefDate]     = useState(new Date());
  const [genreFilter, setGenreFilter] = useState(null);
  const [stats,       setStats]       = useState(null);
  const [loading,     setLoading]     = useState(false);

  const navigate = useCallback((dir) => {
    setRefDate(prev => {
      const d     = new Date(prev);
      const delta = dir === 'prev' ? -1 : 1;
      if      (period === 'jour')    d.setDate(d.getDate() + delta);
      else if (period === 'semaine') d.setDate(d.getDate() + delta * 7);
      else if (period === 'mois')    d.setMonth(d.getMonth() + delta);
      else                           d.setFullYear(d.getFullYear() + delta);
      return d;
    });
  }, [period]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = refDate.toISOString().split('T')[0];
      const res = await apiClient.get('/api/Dashboard/stats', {
        params: {
          period,
          date: dateStr,
          ...(genreFilter ? { genre: genreFilter } : {}),
        },
      });
      setStats(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [period, refDate, genreFilter]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const decl  = stats?.declarations;
  const users = stats?.utilisateurs;
  const gTotal = decl
    ? decl.byGenre.homme + decl.byGenre.femme + decl.byGenre.enfant + decl.byGenre.inconnu
    : 0;

  return (
    <div>
      {/* ── Controls bar ──────────────────────────────── */}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1.5rem' }}>

        {/* Period buttons */}
        <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 8, padding: 3, gap: 2 }}>
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => { setPeriod(p.key); setRefDate(new Date()); }}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: 'none',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                letterSpacing: '0.05em',
                background: period === p.key ? 'var(--green)' : 'transparent',
                color:      period === p.key ? '#fff' : '#6b7280',
                transition: 'all 0.15s',
              }}
            >{p.label}</button>
          ))}
        </div>

        {/* Date navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => navigate('prev')} style={navBtn}>‹</button>
          <span style={{ fontSize: 15, fontWeight: 600, minWidth: 180, textAlign: 'center' }}>
            {getDateLabel(period, refDate)}
          </span>
          <button onClick={() => navigate('next')} style={navBtn}>›</button>
          {!isCurrentPeriod(period, refDate) && (
            <button onClick={() => setRefDate(new Date())} style={todayBtnStyle}>Aujourd'hui</button>
          )}
        </div>

        {/* Genre filter */}
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { key: null,     label: 'Tous'   },
            { key: 'homme',  label: 'Homme'  },
            { key: 'femme',  label: 'Femme'  },
            { key: 'enfant', label: 'Enfant' },
          ].map(g => (
            <button
              key={g.key ?? 'tous'}
              onClick={() => setGenreFilter(g.key)}
              style={{
                padding: '5px 12px',
                borderRadius: 20,
                border: `1.5px solid ${genreFilter === g.key ? 'var(--green)' : '#d1d5db'}`,
                background: genreFilter === g.key ? 'var(--green)' : 'transparent',
                color:      genreFilter === g.key ? '#fff' : '#4b5563',
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >{g.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>Chargement…</div>
      ) : (
        <div>
          {/* ── Charts row ──────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem', marginBottom: 0 }}>
            <StatCard label="Janazas déclarées" total={decl?.total} color={DECL_COLOR}>
              {decl?.series && <AreaChart series={decl.series} color={DECL_COLOR} gradId="grad-decl" />}
            </StatCard>

            <StatCard label="Nouvelles inscriptions" total={users?.total} color={USER_COLOR}>
              {users?.series && <AreaChart series={users.series} color={USER_COLOR} gradId="grad-users" />}
            </StatCard>
          </div>

          {/* ── Genre + Country + Mosque ────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginTop: '1rem' }}>

            {decl && (
              <SectionCard title="Par genre">
                <ProgressRow label="Homme"         count={decl.byGenre.homme}   total={gTotal} color={GENRE_COLORS.homme}  dot />
                <ProgressRow label="Femme"         count={decl.byGenre.femme}   total={gTotal} color={GENRE_COLORS.femme}  dot />
                <ProgressRow label="Enfant"        count={decl.byGenre.enfant}  total={gTotal} color={GENRE_COLORS.enfant} dot />
                <ProgressRow label="Non renseigné" count={decl.byGenre.inconnu} total={gTotal} color={GENRE_COLORS.inconnu}dot />
              </SectionCard>
            )}

            {decl?.byPays?.length > 0 && (
              <SectionCard title="Par pays">
                {decl.byPays.map((item, i) => (
                  <ProgressRow key={i} label={item.pays} count={item.count}
                    total={decl.byPays[0].count} color={DECL_COLOR} />
                ))}
              </SectionCard>
            )}

            {decl?.byMosquee?.length > 0 && (
              <SectionCard title="Par mosquée">
                {decl.byMosquee.map((item, i) => (
                  <ProgressRow key={i} label={item.nom} count={item.count}
                    total={decl.byMosquee[0].count} color="var(--green)" />
                ))}
              </SectionCard>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const navBtn = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: '1.5px solid #d1d5db',
  background: '#fff',
  fontSize: 18,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  color: 'var(--green)',
  lineHeight: 1,
};

const todayBtnStyle = {
  padding: '5px 12px',
  borderRadius: 8,
  border: '1.5px solid var(--green)',
  background: 'transparent',
  color: 'var(--green)',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
